import React, { ChangeEvent, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { get_user_profile, update_profile } from './api/profileApi';
import { useNavigate } from 'react-router-dom';
import { validateImage } from '../../helper-functions/ImageValidation';
import {LoadingProgressBar} from '../../pages/projects/components/LoadingProgressBar';

// Helper function to get full profile picture URL
const getProfilePictureUrl = (pictureUrl: string | null | undefined): string | null => {
  if (!pictureUrl) return null;
  
  // If already a full URL (starts with http:// or https://), return as is
  if (pictureUrl.startsWith('http://') || pictureUrl.startsWith('https://')) {
    return pictureUrl;
  }
  
  // Otherwise, prepend API base URL
  let baseURL = process.env.REACT_APP_API_URL || '';
  if (!baseURL) return pictureUrl;
  
  // Remove /api from the end of baseURL if present
  baseURL = baseURL.replace(/\/api\/?$/, '');
  
  // Remove trailing slash from baseURL and leading slash from pictureUrl
  const cleanBaseURL = baseURL.replace(/\/$/, '');
  const cleanPictureUrl = pictureUrl.replace(/^\//, '');
  
  return `${cleanBaseURL}/${cleanPictureUrl}`;
};

const ProfileHeader: React.FC = () => {
  const [userPicture, setUserPicture] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Get user profile from API or localStorage
  const { data: ViewProfileDetails, isLoading: viewIsLoading, error: viewIsError, refetch } = useQuery(
    'ViewProfileDetails',
    get_user_profile,
    { 
      refetchOnWindowFocus: false,
      retry: false,
      onError: () => {
        // Fallback to localStorage if API fails
      }
    }
  );

  // Fallback to localStorage data if API is not available
  const authData = (() => {
    const data = localStorage.getItem('kt-auth-react-v');
    try {
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  })();

  const profileData = ViewProfileDetails?.data || authData?.admin || null;
  const updateProfileMutation = useMutation((formData: FormData) => update_profile(formData), {
    onSuccess: (response) => {
      // Refetch current profile query
      refetch();
      // Invalidate header menu and navbar profile queries to update name and picture in header
      queryClient.invalidateQueries('UserProfileForMenu');
      queryClient.invalidateQueries('UserProfileForNavbar');
      toast.success(response.message, {
        position: 'top-right',
        autoClose: 3000,
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'An unexpected error occurred.';
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
      });
    },
  });
  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required.'),
    title: Yup.string().required('Title is required.'),
  });
  
  const formik = useFormik({
    initialValues: {
      title: '',
      name: '',
      status: '1',
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = new FormData();
      if (values.title) {
        formData.append('title', values.title);
      }
      formData.append('name', values.name);

      // For super admin, always send status as 1, otherwise send selected status
      if (profileData?.is_super_admin === 1) {
        formData.append('status', '1');
      } else if (values.status) {
        formData.append('status', values.status);
      }

      if (userPicture) {
        formData.append('profile_picture', userPicture);
      }

      updateProfileMutation.mutate(formData);
    },
  });

  useEffect(() => {
    if (profileData) {
      // API now returns 'name' field instead of 'full_name'
      const userName = profileData.name || profileData.full_name || '';
      formik.setValues({
        title: profileData.title || '',
        name: userName,
        status: profileData.status?.toString() || '1',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    logoType: string,
    MAX_FILE_SIZE: number,
    ALLOWED_TYPES: string[],
    MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
  ) => {
    const newFile = e.target.files?.[0];
    if (newFile) {
      // Call the validation function
      validateImage(
        newFile,
        logoType,
        (validFileUrl) => {
          // If valid, update the state with the image URL
          if (logoType === 'picture') {
            setUserPicture(newFile);
          }
        },
        MAX_FILE_SIZE, // File size limit in MB
        ALLOWED_TYPES, // Allowed file types
        MAX_DIMENSIONS // Maximum dimensions per logo type
      );
    }
  };

  // Show skeleton while loading
  if (viewIsLoading && !profileData) {
    return (
      <div className="card mb-5 mb-xl-10">
        <LoadingProgressBar isLoading={viewIsLoading} />
        <div className="card-header text-center border-0 pt-4 pb-4 bg-light">
          <div className="placeholder placeholder-lg bg-light w-150px h-35px rounded mx-auto"></div>
        </div>
        <div className="card-body pt-9 pb-0">
          {/* Name Skeleton */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-bold fs-6'>
              <div className="placeholder placeholder-lg bg-light w-80px"></div>
            </label>
            <div className='col-lg-9'>
              <div className="placeholder placeholder-lg bg-light w-100"></div>
            </div>
          </div>

          {/* Email Skeleton */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-bold fs-6'>
              <div className="placeholder placeholder-lg bg-light w-80px"></div>
            </label>
            <div className='col-lg-9'>
              <div className="placeholder placeholder-lg bg-light w-100"></div>
            </div>
          </div>

          {/* Picture Upload Skeleton */}
          <div className="row mb-6">
            <label className="col-lg-3 col-form-label fw-bold fs-6">
              <div className="placeholder placeholder-lg bg-light w-70px"></div>
            </label>
            <div className="col-lg-9">
              <div className="placeholder placeholder-lg bg-light w-100 h-40px"></div>
            </div>
          </div>

          {/* Existing Picture Skeleton */}
          <div className="row mb-6">
            <label className="col-lg-3 col-form-label fw-bold fs-6">
              <div className="placeholder placeholder-lg bg-light w-120px"></div>
            </label>
            <div className="col-lg-9">
              <div className="placeholder placeholder-lg bg-light w-100px h-100px rounded-circle"></div>
            </div>
          </div>

          {/* Submit Button Skeleton */}
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <div className="placeholder placeholder-lg bg-light w-120px h-40px rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (viewIsError) {
    return (
      <div className="text-center">
        <h2>Error fetching profile details</h2>
      </div>
    );
  }

  return (
    <div className="card mb-5 mb-xl-10">
      {/* Loading Progress Bar */}
      <LoadingProgressBar isLoading={viewIsLoading || updateProfileMutation.isLoading} />
      
      <div className="card-header text-center border-0 pt-4 pb-4 bg-light">
        <button
          data-toggle="tooltip"
          data-placement="bottom"
          title="Navigate to Dashboard"
          className="btn btn-sm btn-light-dark fs-5"
          onClick={() => navigate('/dashboard')}
        >
          Return to Dashboard
        </button>
      </div>
      <div className="card-body pt-9 pb-0">
        <form onSubmit={formik.handleSubmit}>
          {/* Title */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Title</label>
            <div className='col-lg-9'>
              <select
                className={`form-control form-control-lg form-control-solid ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                {...formik.getFieldProps('title')}
              >
                <option value="">Select Title</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
              </select>
              {formik.touched.title && formik.errors.title && (
                <div className="text-danger">{formik.errors.title}</div>
              )}
            </div>
          </div>

          {/* Name */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label required fw-bold fs-6'>Name</label>
            <div className='col-lg-9'>
              <input
                type="text"
                className={`form-control form-control-lg form-control-solid ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                placeholder="Name"
                value={formik.values.name || profileData?.name || profileData?.full_name || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="name"
              />
              {formik.touched.name && formik.errors.name && (
                <div className="text-danger">{formik.errors.name}</div>
              )}
            </div>
          </div>

          {/* Email */}
          <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-bold fs-6'>
              <span className='required'>Email</span>
            </label>
            <div className='col-lg-9 fv-row'>
              <label className='form-control form-control-lg form-control-solid user-input'>
                {profileData?.email || 'N/A'}
              </label>
            </div>
          </div>

          {/* Status - Only show if user is not super admin */}
          {profileData?.is_super_admin !== 1 && (
            <div className='row mb-6'>
              <label className='col-lg-3 col-form-label required fw-bold fs-6'>Status</label>
              <div className='col-lg-9'>
                <select
                  className={`form-control form-control-lg form-control-solid ${formik.touched.status && formik.errors.status ? 'is-invalid' : ''}`}
                  {...formik.getFieldProps('status')}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="text-danger">{formik.errors.status}</div>
                )}
              </div>
            </div>
          )}

          {/* TODO: Uncomment when needed */}
          {/* First Name */}
          {/* <div className="row mb-6">
            <label className="col-lg-3 col-form-label required fw-bold fs-6">First Name</label>
            <div className="col-lg-9">
              <input
                type="text"
                className={`form-control form-control-lg form-control-solid ${formik.touched.first_name && formik.errors.first_name ? 'is-invalid' : ''}`}
                placeholder="First Name"
                {...formik.getFieldProps('first_name')}
              />
              {formik.touched.first_name && formik.errors.first_name && (
                <div className="text-danger">{formik.errors.first_name}</div>
              )}
            </div>
          </div> */}
          
          {/* Last Name */}
          {/* <div className="row mb-6">
            <label className="col-lg-3 col-form-label fw-bold fs-6">Last Name</label>
            <div className="col-lg-9">
              <input
                type="text"
                className={`form-control form-control-lg form-control-solid ${formik.touched.last_name && formik.errors.last_name ? 'is-invalid' : ''}`}
                placeholder="Last Name"
                {...formik.getFieldProps('last_name')}
              />
            </div>
          </div> */}

          {/* Role */}
          {/* <div className='row mb-6'>
            <label className='col-lg-3 col-form-label fw-bold fs-6'>
              <span className='required'>Role</span>
            </label>
            <div className='col-lg-9 fv-row'>
              <label className='form-control form-control-lg form-control-solid user-input'>
                {profileData?.role || 'N/A'}
              </label>
            </div>
          </div> */}

          {/* Picture Upload */}
          <div className="row mb-6">
            <label className="col-lg-3 col-form-label fw-bold fs-6">Picture</label>
            <div className="col-lg-9 fv-row">
              <input
                type="file"
                className="form-control form-control-lg form-control-solid"
                onChange={(e) => handleImageChange(
                  e,
                  'picture',
                  2, // Max file size (2MB)
                  ['image/jpeg', 'image/jpg', 'image/png'], // Allowed types
                  {
                    picture: { width: 200, height: 200 }
                  } // Max dimensions
                )}
                accept="image/jpeg, image/jpg, image/png"
                name='picture'
              />
              <div className='text-danger mx-6 mt-2'>
                Recommended size: 200 x 200 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
              </div>
            </div>
          </div>

          {/* Existing Picture */}
          <div className="row mb-6">
            <label className="col-lg-3 col-form-label fw-bold fs-6">Existing Picture</label>
            <div className="col-lg-9 d-flex align-items-center">
              {profileData?.profile_picture ? (
                <img
                  src={getProfilePictureUrl(profileData.profile_picture) || ''}
                  alt="profile picture"
                  className="img-fluid cover"
                  width={100}
                  height={100}
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                  aria-readonly
                  onError={(e) => {
                    // Fallback to blank image if URL fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = '/media/avatars/blank.png';
                  }}
                />
              ) : (
                <span className="text-muted mx-6 mb-1">No picture uploaded yet</span>
              )}
            </div>
          </div>
          {/* Submit Button */}
          <div className="card-footer d-flex justify-content-end py-6 px-9">
            <button type="submit" className="btn btn-primary" disabled={updateProfileMutation.isLoading}>
              {updateProfileMutation.isLoading ? (
                <>
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </>
              ) : (
                'Update Profile'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { ProfileHeader };

